import { useState } from 'react'
import { ArrowClockwise } from '@phosphor-icons/react'
import { useUsageMetrics } from '@/hooks/useUsageMetrics'
import type { TimeRange } from '@/api/usage'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

// ---------- Helpers ----------

function timeAgo(epochMs: number): string {
  const diffMs = Date.now() - epochMs
  const diffSec = Math.floor(diffMs / 1000)

  if (diffSec < 5) return 'just now'
  if (diffSec < 60) return `${diffSec}s ago`

  const diffMin = Math.floor(diffSec / 60)
  if (diffMin === 1) return '1 min ago'
  if (diffMin < 60) return `${diffMin} min ago`

  const diffHours = Math.floor(diffMin / 60)
  if (diffHours === 1) return '1 hour ago'
  return `${diffHours} hours ago`
}

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
]

// ---------- Inline components ----------

function StatCard({
  label,
  value,
  subtext,
}: {
  label: string
  value: string
  subtext?: string
}) {
  return (
    <Card className="flex-1 min-w-[200px]">
      <CardContent className="pt-4 pb-3 px-4">
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
        {subtext && (
          <p className="text-xs text-muted-foreground mt-1">{subtext}</p>
        )}
      </CardContent>
    </Card>
  )
}

function StatCardSkeleton() {
  return (
    <Card className="flex-1 min-w-[200px]">
      <CardContent className="pt-4 pb-3 px-4">
        <Skeleton className="h-3 w-20 mb-2" />
        <Skeleton className="h-7 w-24" />
        <Skeleton className="h-3 w-28 mt-2" />
      </CardContent>
    </Card>
  )
}

function TableSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <Skeleton className="h-8 w-full mb-2" />
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full mb-2" />
        ))}
      </CardContent>
    </Card>
  )
}

// ---------- Usage Page ----------

export function Usage() {
  const [range, setRange] = useState<TimeRange>('7d')
  const [orgId, setOrgId] = useState<string | undefined>(undefined)

  const { data, isLoading, dataUpdatedAt, isRefetchError, refetch, isFetching, isError, error } =
    useUsageMetrics({ range, org_id: orgId })

  const handleOrgChange = (value: string) => {
    setOrgId(value === '__all__' ? undefined : value)
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            Usage &amp; Costs
          </h2>
          <p className="text-sm text-muted-foreground">
            Agent usage metrics and cost breakdown across tenants
          </p>
        </div>
        <div className="flex items-center gap-3">
          {dataUpdatedAt > 0 && (
            <span className="text-xs text-muted-foreground">
              Updated {timeAgo(dataUpdatedAt)}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowClockwise
              className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')}
            />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stale data warning banner */}
      {isRefetchError && !!data && (
        <div className="rounded-md border border-yellow-500/50 bg-yellow-500/10 px-4 py-2 text-sm text-yellow-700 dark:text-yellow-400">
          Data may be stale — last successful refresh was{' '}
          {timeAgo(dataUpdatedAt)}.
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Time Range Buttons */}
        <div className="flex items-center gap-1">
          {TIME_RANGE_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              variant={range === opt.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setRange(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>

        {/* Org Filter */}
        <Select
          value={orgId ?? '__all__'}
          onValueChange={handleOrgChange}
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="All Organizations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Organizations</SelectItem>
            {data?.org_breakdown.map((org) => (
              <SelectItem key={org.org_id} value={org.org_id}>
                {org.org_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Error State */}
      {isError && !data && (
        <Card className="border-destructive">
          <CardContent className="py-6 text-center">
            <p className="text-sm text-destructive font-medium">
              Failed to load usage data
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Stat Cards */}
      <div className="flex flex-wrap gap-3">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : data ? (
          <>
            <StatCard
              label="Conversations"
              value={data.summary.conversations.toLocaleString()}
              subtext="conversations"
            />
            <StatCard
              label="Total Tokens"
              value={data.summary.total_tokens.toLocaleString()}
              subtext="tokens consumed"
            />
            <StatCard
              label="Estimated Cost"
              value={`$${data.summary.estimated_cost.toFixed(2)}`}
              subtext="estimated cost"
            />
            <StatCard
              label="Error Rate"
              value={`${data.summary.error_rate_percent.toFixed(1)}%`}
              subtext={`${data.summary.error_count} errors of ${data.summary.total_requests} requests`}
            />
          </>
        ) : null}
      </div>

      {/* Org Breakdown Table */}
      <div>
        <h3 className="text-base font-semibold mb-3">
          Usage by Organization
        </h3>
        {isLoading ? (
          <TableSkeleton rows={3} />
        ) : data ? (
          data.org_breakdown.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No usage data for selected period
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-0 px-0 pb-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organization</TableHead>
                      <TableHead className="text-right">Conversations</TableHead>
                      <TableHead className="text-right">Tokens</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-right">Errors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.org_breakdown.map((org) => (
                      <TableRow key={org.org_id}>
                        <TableCell className="font-medium">
                          {org.org_name}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {org.conversations.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {org.total_tokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          ${org.estimated_cost.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {org.error_count}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )
        ) : null}
      </div>

      {/* Model Cost Breakdown Table */}
      <div>
        <h3 className="text-base font-semibold mb-3">Cost by Model</h3>
        {isLoading ? (
          <TableSkeleton rows={2} />
        ) : data ? (
          data.model_breakdown.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No model data for selected period
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-0 px-0 pb-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Model</TableHead>
                      <TableHead className="text-right">Requests</TableHead>
                      <TableHead className="text-right">Input Tokens</TableHead>
                      <TableHead className="text-right">Output Tokens</TableHead>
                      <TableHead className="text-right">Total Tokens</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.model_breakdown.map((model) => (
                      <TableRow key={model.model}>
                        <TableCell className="font-mono text-xs">
                          {model.model}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {model.total_requests.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {model.prompt_tokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {model.completion_tokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {model.total_tokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          ${model.estimated_cost.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )
        ) : null}
      </div>

      {/* Top 5 Users Table */}
      <div>
        <h3 className="text-base font-semibold mb-3">Top 5 Users</h3>
        {isLoading ? (
          <TableSkeleton rows={5} />
        ) : data ? (
          data.top_users.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No user activity for selected period
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-0 px-0 pb-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-right">Requests</TableHead>
                      <TableHead className="text-right">Tokens</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.top_users.map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell className="font-medium">
                          {user.user_name}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {user.user_email}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {user.request_count.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {user.total_tokens.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )
        ) : null}
      </div>
    </div>
  )
}
